import { formatTimeAgoSec } from "@breakout/shared/utils/time"
import { cn, formatShares, formatUsd } from "@breakout/ui/utils"
import { Link } from "@tanstack/react-router"
import { ChevronDown, ChevronUp, Clock, ExternalLink, Loader2 } from "lucide-react"
import { memo, useState } from "react"
import type { Address } from "viem"
import { getAddress } from "viem"
import { useInfiniteScroll } from "../../hooks/common/useInfiniteScroll"
import { useUserActivityPaginated } from "../../hooks/indexer/useUserActivityPaginated"
import { breakoutChain } from "../../lib/providers"
import { EmptyState } from "../ui/EmptyState"
import { SimpleAvatar } from "../ui/SimpleAvatar"
import { Skeleton } from "../ui/Skeleton"

type SortField = "time" | "amount"
type SortDirection = "asc" | "desc"

interface ProfileActivityFeedProps {
	userAddress: Address | null
}

export const ProfileActivityFeed = memo(function ProfileActivityFeed({
	userAddress,
}: ProfileActivityFeedProps) {
	const [sortField, setSortField] = useState<SortField>("time")
	const [sortDirection, setSortDirection] = useState<SortDirection>("desc")

	// Pass sort params to backend for efficient paginated sorting
	const { results, status, loadMore, isLoading, hasMore } = useUserActivityPaginated(
		userAddress
			? {
					userAddress,
					sortBy: sortField,
					sortDirection,
				}
			: null,
	)

	const loadMoreRef = useInfiniteScroll({
		hasMore,
		status,
		loadMore,
	})

	const showLoadingSkeleton = (isLoading && results.length === 0) || status === "LoadingFirstPage"

	const handleSort = (field: SortField) => {
		if (sortField === field) {
			setSortDirection(prev => (prev === "desc" ? "asc" : "desc"))
		} else {
			setSortField(field)
			setSortDirection("desc")
		}
	}

	if (showLoadingSkeleton) {
		return (
			<div>
				<ActivityHeaderSkeleton />
				<div className="space-y-3 sm:space-y-4">
					{[0, 1, 2].map(i => (
						<ActivityRowSkeleton key={i} />
					))}
				</div>
			</div>
		)
	}

	if (results.length === 0) {
		return (
			<EmptyState
				action={{ label: "Browse Markets", href: "/" }}
				compact
				description="Trading history will appear here once there's activity."
				icon={Clock}
				title="No Activity Yet"
			/>
		)
	}

	return (
		<div>
			<ActivityHeader onSort={handleSort} sortDirection={sortDirection} sortField={sortField} />
			<div className="space-y-3 sm:space-y-4">
				{results.map((activity, index) => (
					<ProfileActivityRow
						activity={activity}
						key={`${activity.marketAddress}-${activity.blockTimestampSec}-${index}`}
					/>
				))}
			</div>

			{/* Load more sentinel */}
			<div className="py-4" ref={loadMoreRef}>
				{status === "LoadingMore" && (
					<div className="flex items-center justify-center gap-2 text-sm text-text-muted">
						<Loader2 aria-hidden="true" className="size-4 animate-spin" />
						<span>Loading more…</span>
					</div>
				)}
			</div>
		</div>
	)
})

// =============================================================================
// HEADER
// =============================================================================

interface ActivityHeaderProps {
	sortField: SortField
	sortDirection: SortDirection
	onSort: (field: SortField) => void
}

function ActivityHeader({ sortField, sortDirection, onSort }: ActivityHeaderProps) {
	// Sort button for Amount
	const AmountSortButton = ({ className = "" }: { className?: string }) => (
		<button
			aria-label={`Sort by amount, currently ${sortField === "amount" ? sortDirection : "unsorted"}`}
			className={`flex items-center gap-1 text-text-muted transition-colors hover:text-text-primary ${className}`}
			onClick={() => onSort("amount")}
			type="button"
		>
			<span>Amount</span>
			{sortField === "amount" &&
				(sortDirection === "asc" ? (
					<ChevronUp aria-hidden="true" className="size-3.5" />
				) : (
					<ChevronDown aria-hidden="true" className="size-3.5" />
				))}
		</button>
	)

	return (
		<>
			{/* Mobile header - compact sort control */}
			<div className="mb-3 flex items-center justify-end px-1 sm:hidden">
				<AmountSortButton className="rounded-md bg-bg-secondary/50 px-2.5 py-1.5 font-medium text-xs" />
			</div>

			{/* Desktop header - full column labels */}
			<div className="mb-3 hidden items-center gap-3 rounded-lg bg-bg-secondary/50 px-4 py-2 font-medium text-text-muted text-xs sm:flex">
				{/* Type column */}
				<div className="w-8 shrink-0">Type</div>

				{/* Avatar spacer */}
				<div className="size-8 shrink-0" />

				{/* Market column */}
				<div className="min-w-0 flex-1">Market</div>

				{/* Amount column - sortable */}
				<AmountSortButton className="w-20 shrink-0 justify-end" />

				{/* Time column */}
				<div className="w-24 shrink-0 text-right">Time</div>
			</div>
		</>
	)
}

// =============================================================================
// ROW
// =============================================================================

interface ProfileActivityRowProps {
	activity: ReturnType<typeof useUserActivityPaginated>["results"][number]
}

const ProfileActivityRow = memo(function ProfileActivityRow({ activity }: ProfileActivityRowProps) {
	const checksummedAddress = getAddress(activity.marketAddress)
	// avatarUrl is always provided by the backend (with fallbacks applied server-side)
	const timeAgo = formatTimeAgoSec(activity.blockTimestampSec)

	const pricePerShare = activity.shares > 0 ? (activity.usdcAmount / activity.shares) * 100 : 0
	const explorerUrl = `${breakoutChain.blockExplorers?.default.url}/tx/${activity.txHash}`

	return (
		<Link
			className="block rounded-xl border border-border-subtle bg-bg-card p-3 transition-colors hover:bg-bg-secondary/40 sm:p-4"
			params={{ marketId: checksummedAddress }}
			to="/markets/$marketId"
		>
			<div className="flex items-center gap-3">
				{/* Buy/Sell column */}
				<span
					className={`w-8 shrink-0 font-semibold text-sm ${
						activity.isBuy ? "text-breakout-brand" : "text-red-600 dark:text-red-500"
					}`}
				>
					{activity.isBuy ? "Buy" : "Sell"}
				</span>

				{/* Avatar */}
				<SimpleAvatar
					alt={activity.displayName || "Market avatar"}
					className="shrink-0"
					size="sm"
					src={activity.avatarUrl}
					variant="square"
				/>

				{/* Market + Outcome */}
				<div className="min-w-0 flex-1">
					<p className="truncate font-medium text-sm text-text-primary">{activity.displayName}</p>
					<p className="mt-0.5 truncate text-text-secondary text-xs">
						<span
							className={cn(
								"font-semibold",
								activity.bucket === 0 ? "text-breakout-brand" : "text-red-600 dark:text-red-500",
							)}
						>
							{activity.displayLabel}
						</span>
						<span className="tabular-nums">
							{" "}
							{formatShares(activity.shares)} shares at {pricePerShare.toFixed(0)}¢
						</span>
					</p>
				</div>

				{/* Amount - w-20 matches header */}
				<p className="w-20 shrink-0 text-right font-semibold text-sm text-text-primary tabular-nums">
					{formatUsd(activity.usdcAmount)}
				</p>

				{/* Time + Explorer link - w-24 matches header */}
				<div className="hidden w-24 shrink-0 items-center justify-end gap-1.5 sm:flex">
					<span className="text-text-muted text-xs tabular-nums">{timeAgo}</span>
					<a
						aria-label="View transaction on block explorer"
						className="rounded p-1 text-text-muted transition-colors hover:bg-bg-tertiary hover:text-text-secondary"
						href={explorerUrl}
						onClick={e => e.stopPropagation()}
						rel="noopener noreferrer"
						target="_blank"
					>
						<ExternalLink aria-hidden="true" className="size-3.5" />
					</a>
				</div>
			</div>
		</Link>
	)
})

// =============================================================================
// SKELETON
// =============================================================================

/**
 * Skeleton for ActivityHeader - matches header layout to prevent layout shift
 * Shows on both mobile (compact sort) and desktop (full header)
 */
function ActivityHeaderSkeleton() {
	return (
		<>
			{/* Mobile skeleton - compact sort control */}
			<div className="mb-3 flex items-center justify-end px-1 sm:hidden">
				<Skeleton className="h-[30px] w-[76px] rounded-md" />
			</div>

			{/* Desktop skeleton - full header row */}
			<div className="mb-3 hidden items-center gap-3 rounded-lg bg-bg-secondary/50 px-4 py-2 sm:flex">
				{/* Type column */}
				<div className="w-8 shrink-0">
					<Skeleton className="h-3 w-6" />
				</div>
				{/* Avatar spacer */}
				<div className="size-8 shrink-0" />
				{/* Market column */}
				<div className="min-w-0 flex-1">
					<Skeleton className="h-3 w-12" />
				</div>
				{/* Amount column */}
				<div className="w-20 shrink-0 text-right">
					<Skeleton className="ml-auto h-3 w-12" />
				</div>
				{/* Time column */}
				<div className="w-24 shrink-0 text-right">
					<Skeleton className="ml-auto h-3 w-8" />
				</div>
			</div>
		</>
	)
}

function ActivityRowSkeleton() {
	// Matches ProfileActivityRow exactly:
	// - Buy/Sell: text-sm font-semibold = h-5
	// - Avatar: size-8 with rounded-md (SimpleAvatar sm + square)
	// - Market name: text-sm font-medium = h-5
	// - Details: text-xs = h-4
	// - Amount: text-sm font-semibold = h-5
	// - Time: text-xs = h-4 (desktop only)
	return (
		<div className="rounded-xl border border-border-subtle bg-bg-card p-3 sm:p-4">
			<div className="flex items-center gap-3">
				{/* Buy/Sell - text-sm font-semibold = h-5 */}
				<Skeleton className="h-5 w-8 shrink-0" />

				{/* Avatar - size-8 with rounded-md (sm + square variant) */}
				<Skeleton className="size-8 shrink-0 rounded-md" />

				{/* Market + Outcome */}
				<div className="min-w-0 flex-1">
					{/* Market name - text-sm font-medium = h-5 */}
					<Skeleton className="h-5 w-28" />
					{/* Details - text-xs = h-4 */}
					<Skeleton className="mt-1 h-4 w-36" />
				</div>

				{/* Amount - w-20 matches header */}
				<Skeleton className="h-5 w-20 shrink-0" />

				{/* Time + icon - w-24 matches header */}
				<div className="hidden w-24 shrink-0 items-center justify-end gap-1.5 sm:flex">
					<Skeleton className="h-4 w-12" />
					{/* External link icon placeholder */}
					<div className="size-[22px]" />
				</div>
			</div>
		</div>
	)
}
